import React from 'react';

import { Box, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import ClickablePhone from 'app/shared/components/ClickablePhone';

import { NOT_APPLICABLE_TEXT, PARTY_COLUMN_LABELS, PartyColumnId } from './Parties.constants';
import {
  contactEmailStyles,
  partyCellStyles,
  partyNameStyles,
  pendingLabelStyles,
} from './Parties.styles';
import { getPartyDriveStatus } from './Parties.utils';
import PartyActionButton from './PartyActionButton';
import PartyDriveCell from './PartyDriveCell';

const renderText = value => (
  <Typography color="text.secondary" variant="regular14">
    {value || NOT_APPLICABLE_TEXT}
  </Typography>
);

/**
 * @param {Object} args
 * @param {{ preview: Object|null, previewIndex: Object }} args.drivePreview
 * @param {string} args.applicationId
 * @param {boolean} args.isActionDisabled
 * @param {boolean} args.isContactLocked auto-lock forbids changing loan-file contacts
 * @param {Function} args.onRemove called with the row of a loan-file contact to remove
 * @returns {Object} TableCDS config
 */
export const getPartiesTableConfig = ({
  drivePreview,
  applicationId,
  isActionDisabled,
  isContactLocked,
  onRemove,
}) => ({
  [PartyColumnId.PARTY]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.PARTY],
    width: '18%',
    content: row => (
      <Box sx={partyCellStyles}>
        <Box sx={partyNameStyles}>
          <Typography variant="medium14">{row.name}</Typography>
          {!!row.pendingLabel && (
            <Box sx={pendingLabelStyles}>
              <LabelCDS size={LabelSize.S} title={row.pendingLabel} variant={LabelVariant.ORANGE} />
            </Box>
          )}
        </Box>
        {!!row.detail && (
          <Typography color="text.secondary" variant="regular12">
            {row.detail}
          </Typography>
        )}
      </Box>
    ),
  },
  [PartyColumnId.CONTACT_TYPE]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.CONTACT_TYPE],
    width: '12%',
    content: row => renderText(row.contactType),
  },
  [PartyColumnId.SOURCE]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.SOURCE],
    width: '11%',
    content: row => renderText(row.source),
  },
  [PartyColumnId.CONTACT_INFO]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.CONTACT_INFO],
    width: '22%',
    content: row => (
      <Box sx={partyCellStyles}>
        <Typography color="text.secondary" sx={contactEmailStyles} variant="regular14">
          {row.email || NOT_APPLICABLE_TEXT}
        </Typography>
        <ClickablePhone
          applicationId={applicationId}
          emptyFallback={NOT_APPLICABLE_TEXT}
          typographyVariant="regular14"
          value={row.phone}
        />
      </Box>
    ),
  },
  [PartyColumnId.ADDRESS]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.ADDRESS],
    width: '16%',
    content: row => renderText(row.address),
  },
  [PartyColumnId.DRIVE]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.DRIVE],
    width: '13%',
    content: row => {
      const { state, text } = getPartyDriveStatus(row, drivePreview);

      return <PartyDriveCell state={state} text={text} />;
    },
  },
  [PartyColumnId.ACTION]: {
    label: PARTY_COLUMN_LABELS[PartyColumnId.ACTION],
    width: '8%',
    align: 'center',
    content: row => (
      <PartyActionButton
        isContactLocked={isContactLocked}
        isDisabled={isActionDisabled}
        row={row}
        onRemove={onRemove}
      />
    ),
  },
});
